import { useEffect, useRef, useState, useCallback } from 'react';

const PHOTOS = [
  { url: 'https://public.readdy.ai/ai/img_res/edited_f9d9b61d9de9112ea9e8cce1b8b191f6_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_8ef85cefc208a0e233d4196510485d5f_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_51549a54aa5e9d1baf3fc8ff45215162_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_0ce50bc4ac70bbfdd4a2578420da6616_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_58769d38f735176ff83302215df84dc3_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_7a8a4b6615fd5ae13eb2b720762a5e16_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_13ca805033c0bd5fcc820e72e5f907fb_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_125447f3d6beae9600715d023a46cee7_68002c5d.jpg' },
  { url: 'https://public.readdy.ai/ai/img_res/edited_223d326ac20cf3f484297d8af4bb0966_68002c5d.jpg' },
  { url: 'https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/dcafd28c-dcc8-4a90-b8ae-d4211fbe6f25_KakaoTalk_20260402_172824636.jpg?v=c5e7ac6db91ca430a20f72c09dddfeb1' },
];

export default function GallerySection() {
  const ref = useRef<HTMLElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [paused, setPaused] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback((idx: number) => {
    if (idx === activeIdx || fading) return;
    setFading(true);
    setTimeout(() => {
      setActiveIdx(idx);
      setFading(false);
    }, 220);
    // 페이지 전체 스크롤 방지: 썸네일 컨테이너 내부에서만 가로 스크롤
    const container = thumbsRef.current;
    const thumb = container?.children[idx] as HTMLElement;
    if (container && thumb) {
      const containerLeft = container.scrollLeft;
      const containerWidth = container.clientWidth;
      const thumbLeft = thumb.offsetLeft;
      const thumbWidth = thumb.offsetWidth;
      const targetScroll = thumbLeft - containerWidth / 2 + thumbWidth / 2;
      container.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  }, [activeIdx, fading]);

  const goPrev = useCallback(() => goTo((activeIdx - 1 + PHOTOS.length) % PHOTOS.length), [activeIdx, goTo]);
  const goNext = useCallback(() => goTo((activeIdx + 1) % PHOTOS.length), [activeIdx, goTo]);

  // Auto-play: advances every 3.5s, pauses on hover or lightbox
  useEffect(() => {
    if (paused || lightbox) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    autoPlayRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = (prev + 1) % PHOTOS.length;
        // 페이지 전체 스크롤 방지: 썸네일 컨테이너 내부에서만 가로 스크롤
        const container = thumbsRef.current;
        const thumb = container?.children[next] as HTMLElement;
        if (container && thumb) {
          const containerWidth = container.clientWidth;
          const thumbLeft = thumb.offsetLeft;
          const thumbWidth = thumb.offsetWidth;
          const targetScroll = thumbLeft - containerWidth / 2 + thumbWidth / 2;
          container.scrollTo({ left: targetScroll, behavior: 'smooth' });
        }
        return next;
      });
    }, 3500);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [paused, lightbox]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') setLightbox(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  const prevIdx = (activeIdx - 1 + PHOTOS.length) % PHOTOS.length;
  const nextIdx = (activeIdx + 1) % PHOTOS.length;
  const prevPrevIdx = (activeIdx - 2 + PHOTOS.length) % PHOTOS.length;
  const nextNextIdx = (activeIdx + 2) % PHOTOS.length;

  return (
    <section id="gallery" ref={ref} className="bg-[#FAFAF5] py-20 px-7 md:py-28 md:px-8 overflow-hidden">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14 md:mb-18 section-reveal">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 sm:w-16 bg-gold/50"></div>
            <span className="text-gold font-sans text-xs sm:text-sm font-semibold tracking-[0.2em] sm:tracking-[0.25em] uppercase">Clinic Gallery</span>
            <div className="h-px w-12 sm:w-16 bg-gold/50"></div>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-3">클리닉 갤러리</h2>
          <p className="font-sans text-gray-400 text-xs sm:text-sm tracking-wide">
            Gallery · 诊所画廊 · クリニックギャラリー
          </p>
        </div>

        {/* 3-up Portrait Carousel */}
        <div
          className="section-reveal"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Desktop: 5-card layout / Mobile: center only */}
          <div className="relative flex items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8" style={{ height: '420px' }}>

            {/* Far Left — desktop only */}
            <div
              className="hidden lg:block flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 opacity-20 hover:opacity-35"
              style={{ width: '120px', height: '320px' }}
              onClick={goPrev}
            >
              <img src={PHOTOS[prevPrevIdx].url} alt="" className="w-full h-full object-cover object-top" />
            </div>

            {/* Left (prev) — tablet+ only */}
            <div
              className="hidden sm:block flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 opacity-50 hover:opacity-75 hover:scale-[1.02]"
              style={{ width: '180px', height: '360px' }}
              onClick={goPrev}
            >
              <img src={PHOTOS[prevIdx].url} alt={`헤라브로우클리닉 ${prevIdx + 1}`} className="w-full h-full object-cover object-top" />
            </div>

            {/* Center (active) — always visible */}
            <div
              className="relative flex-shrink-0 rounded-2xl overflow-hidden ring-2 ring-gold ring-offset-2 sm:ring-offset-4 ring-offset-[#FAFAF5] transition-all duration-500 cursor-pointer"
              style={{ width: 'min(300px, calc(100% - 40px))', height: '400px' }}
              onClick={() => setLightbox(true)}
            >
              <img
                src={PHOTOS[activeIdx].url}
                alt={`헤라브로우클리닉 ${activeIdx + 1}`}
                className="w-full h-full object-cover object-top transition-opacity duration-300"
                style={{ opacity: fading ? 0 : 1 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4 bg-black/40 backdrop-blur-sm text-white font-sans text-xs px-2.5 py-1 rounded-full tracking-widest">
                {String(activeIdx + 1).padStart(2, '0')} / {String(PHOTOS.length).padStart(2, '0')}
              </div>
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-black/40 backdrop-blur-sm text-white rounded-full">
                <i className="ri-fullscreen-line text-xs"></i>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="font-serif text-white text-sm sm:text-base font-bold">헤라브로우크리닉</p>
                <p className="font-sans text-white/60 text-xs tracking-wider">HERA BROW CLINIC · MYEONGDONG</p>
              </div>
            </div>

            {/* Right (next) — tablet+ only */}
            <div
              className="hidden sm:block flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 opacity-50 hover:opacity-75 hover:scale-[1.02]"
              style={{ width: '180px', height: '360px' }}
              onClick={goNext}
            >
              <img src={PHOTOS[nextIdx].url} alt={`헤라브로우클리닉 ${nextIdx + 1}`} className="w-full h-full object-cover object-top" />
            </div>

            {/* Far Right — desktop only */}
            <div
              className="hidden lg:block flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 opacity-20 hover:opacity-35"
              style={{ width: '120px', height: '320px' }}
              onClick={goNext}
            >
              <img src={PHOTOS[nextNextIdx].url} alt="" className="w-full h-full object-cover object-top" />
            </div>

            {/* Arrow Buttons */}
            <button
              onClick={goPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center bg-white border border-gold/20 text-gray-700 hover:bg-gold hover:text-white hover:border-gold rounded-full cursor-pointer transition-all duration-200 z-10"
            >
              <i className="ri-arrow-left-s-line text-lg sm:text-xl"></i>
            </button>
            <button
              onClick={goNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center bg-white border border-gold/20 text-gray-700 hover:bg-gold hover:text-white hover:border-gold rounded-full cursor-pointer transition-all duration-200 z-10"
            >
              <i className="ri-arrow-right-s-line text-lg sm:text-xl"></i>
            </button>
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-6 sm:mb-8">
            {PHOTOS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`cursor-pointer rounded-full transition-all duration-300 ${
                  idx === activeIdx ? 'w-5 sm:w-6 h-1.5 bg-gold' : 'w-1.5 h-1.5 bg-gray-300 hover:bg-gold/50'
                }`}
              />
            ))}
          </div>

          {/* Thumbnail Strip */}
          <div
            ref={thumbsRef}
            className="flex gap-2 sm:gap-3 justify-center overflow-x-auto pb-1"
            style={{ scrollbarWidth: 'none' }}
          >
            {PHOTOS.map((photo, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`flex-shrink-0 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                  idx === activeIdx
                    ? 'ring-2 ring-gold ring-offset-2 opacity-100'
                    : 'opacity-45 hover:opacity-75'
                }`}
                style={{ width: '52px', height: '68px' }}
              >
                <img src={photo.url} alt={`썸네일 ${idx + 1}`} className="w-full h-full object-cover object-top" />
              </button>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 md:mt-12 section-reveal text-center">
          <button
            onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-plum text-white px-8 py-3.5 rounded-full font-sans font-semibold text-sm cursor-pointer hover:bg-plum-dark transition-colors whitespace-nowrap"
          >
            <i className="ri-calendar-check-line"></i>
            지금 방문 예약하기
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8"
          onClick={() => setLightbox(false)}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white cursor-pointer transition-colors"
            onClick={() => setLightbox(false)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-white/20 hover:border-gold text-white hover:text-gold rounded-full cursor-pointer transition-all"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          <div onClick={(e) => e.stopPropagation()} className="h-[88vh] flex items-center justify-center">
            <img
              src={PHOTOS[activeIdx].url}
              alt="갤러리"
              className="max-h-[88vh] max-w-[80vw] object-contain rounded-xl"
              style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.22s' }}
            />
          </div>

          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-white/20 hover:border-gold text-white hover:text-gold rounded-full cursor-pointer transition-all"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-sans text-white/40 text-xs tracking-widest">
            {String(activeIdx + 1).padStart(2, '0')} — {String(PHOTOS.length).padStart(2, '0')}
          </div>
        </div>
      )}
    </section>
  );
}
